/**
 * Profile photo API client (C-27): the signed-in user's own photo for the
 * header avatar.
 *
 * Routed through the gateway (/api/v1/users/me/photo). The user and tenant are
 * always the JWT's: no call carries a user id, a tenant id or an object path,
 * and the operator Pipeline tenant override is never attached. Any role may set
 * or remove its own photo.
 */

import { config } from '@/config/environment';
import { getAuthToken } from '@/contexts/AuthContext';
import axios from 'axios';

const photoApi = axios.create({
  baseURL: config.GATEWAY_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

photoApi.interceptors.request.use((reqConfig) => {
  const token = getAuthToken();
  if (token) {
    reqConfig.headers.Authorization = `Bearer ${token}`;
  }
  return reqConfig;
});

export const MY_PHOTO_PATH = '/api/v1/users/me/photo';
/** C-28: AVIF too; `.avif` for pickers that type files by name. */
export const PHOTO_ACCEPT = 'image/png,image/webp,image/jpeg,image/avif,.avif';
export const PHOTO_MAX_BYTES = 5 * 1024 * 1024;

export interface UserPhoto {
  /** Signed GCS URL, valid 60 minutes. */
  url: string;
  width: number;
  height: number;
}

interface ErrorShape {
  response?: { status?: number; data?: { detail?: unknown } };
}

const statusOf = (err: unknown) => (err as ErrorShape)?.response?.status;

/** The signed-in user's photo, or null when they have none (a 404). */
export async function fetchMyPhoto(): Promise<UserPhoto | null> {
  try {
    const { data } = await photoApi.get<UserPhoto>(MY_PHOTO_PATH);
    return data;
  } catch (err) {
    if (statusOf(err) === 404) return null;
    throw err;
  }
}

/**
 * The photo of the user whose person record ``canonicalEntityId`` is (the People pages), or null
 * when that person has no user or no photo (a 404). The tenant is the JWT's.
 */
export async function fetchPersonPhoto(canonicalEntityId: string): Promise<UserPhoto | null> {
  try {
    const { data } = await photoApi.get<UserPhoto>(
      `/api/v1/people/${encodeURIComponent(canonicalEntityId)}/photo`,
    );
    return data;
  } catch (err) {
    if (statusOf(err) === 404) return null;
    throw err;
  }
}

/** Replace the signed-in user's photo with ``file``; the server crops, resizes and strips it. */
export async function uploadMyPhoto(file: File): Promise<UserPhoto> {
  const form = new FormData();
  form.append('file', file);
  // Let the browser set the multipart boundary.
  const { data } = await photoApi.post<UserPhoto>(MY_PHOTO_PATH, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

/** Remove the signed-in user's photo; one that is already gone (404) counts as removed. */
export async function removeMyPhoto(): Promise<void> {
  try {
    await photoApi.delete(MY_PHOTO_PATH);
  } catch (err) {
    if (statusOf(err) !== 404) throw err;
  }
}

/** The server's refusal codes (``detail.code``) in plain words. */
const PHOTO_ERRORS: Record<string, string> = {
  unsupported_type: 'Choose a PNG, WebP, JPEG or AVIF image.',
  svg: 'Choose a PNG, WebP, JPEG or AVIF image.',
  too_large: 'That photo is larger than 5 MB. Choose a smaller one.',
  too_many_pixels: 'That photo is larger than 16 megapixels. Choose a smaller one.',
  size_unknown: "We couldn't confirm that photo's size. Export it as a PNG or JPEG, or smaller.",
  animated: "Animated images can't be used. Choose a still photo.",
  corrupt: "We couldn't read that image. Choose another photo.",
  busy: "We're busy right now. Try again in a moment.",
  unavailable: "Photos aren't available right now. Try again in a moment.",
  slow_upload: 'That photo took too long to upload. Try again.',
  rate_limited: "You've changed your photo a lot this hour. Try again later.",
  malformed_upload: "We couldn't read that upload. Choose your photo again.",
};

/** A remove storage could not finish keeps the photo; the words say so. */
const REMOVE_UNAVAILABLE =
  "Your photo wasn't removed: photos aren't available right now. Try again in a moment.";

export const TOO_LARGE_MESSAGE = PHOTO_ERRORS.too_large;
const GENERIC = "Couldn't update your photo. Please try again.";

/**
 * Plain words for a failed photo call. Only a known code is shown; an unknown
 * code or a raw server detail never is. ``action`` is the call that failed.
 */
export function photoErrorMessage(err: unknown, action: 'upload' | 'remove' = 'upload'): string {
  const response = (err as ErrorShape)?.response;
  if (!response) return "Can't reach AskWingo right now. Check your connection and try again.";
  if (response.status === 401) return 'Your session has expired. Sign out and sign in again.';
  const detail = response.data?.detail;
  const code =
    typeof detail === 'object' && detail !== null && 'code' in detail
      ? String((detail as { code: unknown }).code)
      : '';
  if (action === 'remove' && code === 'unavailable') return REMOVE_UNAVAILABLE;
  if (code in PHOTO_ERRORS) return PHOTO_ERRORS[code];
  if (response.status === 413) return PHOTO_ERRORS.too_large;
  return GENERIC;
}
